import { ACTIVIITY_LIST } from 'product-landing-pages/utils/activityList';
import {
  TAXONOMY_LEVEL_1,
  TAXONOMY_LEVEL_2,
  TAXONOMY_LEVEL_3,
} from 'product-landing-pages/utils/taxonomyLevels';

interface SearchConfig {
  filterKey?: string;
  filterValue?: string;
  options?: string;
  query?: string;
  pageLimit?: string;
}

interface ProductFilter {
  activities?: string[];
  activityList?: string[];
  brands?: string[];
  categories?: string[];
  colors?: string[];
  conditions?: string[];
  genders?: string[];
  productTypes?: string[];
  releaseDateEnd?: string[];
  releaseDateStart?: string[];
  releaseYears?: string[];
  silhouettes?: string[];
  sizes?: string[];
  underRetail?: boolean;
}

// Convert YYYYMMDD to MM/DD/YYYY format
const formatDate = (dateStr: string) => {
  const year = dateStr.slice(0, 4);
  const month = dateStr.slice(4, 6);
  const day = dateStr.slice(6, 8);
  return new Date(`${month}/${day}/${year}`).getTime();
};

export const getConsumerSearchFilters = (searchConfig: SearchConfig): ProductFilter => {
  const productFilter: ProductFilter = {};

  // handle filterKey and filterValue
  const lowerCaseFilterValue = searchConfig.filterValue.toLowerCase();

  if (searchConfig.filterKey === 'group_id' && TAXONOMY_LEVEL_1?.includes(lowerCaseFilterValue)) {
    productFilter.categories = [searchConfig.filterValue];
  }

  if (searchConfig.filterKey === 'group_id' && TAXONOMY_LEVEL_2?.includes(lowerCaseFilterValue)) {
    productFilter.productTypes = [searchConfig.filterValue];
  }

  if (searchConfig.filterKey === 'group_id' && TAXONOMY_LEVEL_3?.includes(lowerCaseFilterValue)) {
    productFilter.activities = [searchConfig.filterValue];
  }

  if (searchConfig.filterKey === 'color') {
    productFilter.colors = [searchConfig.filterValue];
  }

  if (searchConfig.filterKey === 'brand') {
    productFilter.brands = [searchConfig.filterValue.replaceAll(' ', '-')];
  }

  if (searchConfig.filterKey === 'discount_tag') {
    productFilter.underRetail = true;
  }

  // handle options string (format: filters[color]=black&filters[brand]=dior)
  if (searchConfig.options) {
    const optionPairs = searchConfig.options.split('&');
    const filterRegex = /filters\[(?<key>\w+)]=(?<value>[^&]+)/;

    for (const pair of optionPairs) {
      const match = pair.match(filterRegex);
      if (!match?.groups) continue;

      const { key, value } = match.groups;

      switch (key) {
        case 'brand':
          productFilter.brands = productFilter.brands || [];
          if (!productFilter.brands.includes(value.replaceAll(' ', '-'))) {
            productFilter.brands.push(value.replaceAll(' ', '-'));
          }
          break;
        case 'color':
          productFilter.colors = productFilter.colors || [];
          if (!productFilter.colors.includes(value)) {
            productFilter.colors.push(value);
          }
          break;
        case 'gender':
          productFilter.genders = productFilter.genders || [];
          if (!productFilter.genders.includes(value)) {
            productFilter.genders.push(value);
          }
          break;
        case 'product_condition':
          productFilter.conditions = productFilter.conditions || [];
          if (!productFilter.conditions.includes(value)) {
            productFilter.conditions.push(value);
          }
          break;
        case 'release_date_year':
          productFilter.releaseYears = productFilter.releaseYears || [];
          if (!productFilter.releaseYears.includes(value)) {
            productFilter.releaseYears.push(value);
          }
          break;
        case 'release_date_year_range': {
          const [min, max] = value.split('-');
          if (Number.isNaN(Number(min)) || Number.isNaN(Number(max))) {
            break;
          }
          for (let i = Number(min); i <= Number(max); i += 1) {
            productFilter.releaseYears = productFilter.releaseYears || [];
            if (!productFilter.releaseYears.includes(i.toString())) {
              productFilter.releaseYears.push(i.toString());
            }
          }
          break;
        }
        case 'release_date': {
          const [min, max] = value.split('-');
          if (Number.isNaN(Number(min)) || Number.isNaN(Number(max))) {
            break;
          }
          const minDate = formatDate(min).toString();
          const maxDate = formatDate(max).toString();
          productFilter.releaseDateStart = productFilter.releaseDateStart || [];
          productFilter.releaseDateEnd = productFilter.releaseDateEnd || [];

          if (!productFilter.releaseDateStart.includes(minDate)) {
            productFilter.releaseDateStart.push(minDate);
          }
          if (!productFilter.releaseDateEnd.includes(maxDate)) {
            productFilter.releaseDateEnd.push(maxDate);
          }
          break;
        }
        case 'silhouette':
          productFilter.silhouettes = productFilter.silhouettes || [];
          if (!productFilter.silhouettes.includes(value.replaceAll(' ', '-'))) {
            productFilter.silhouettes.push(value.replaceAll(' ', '-'));
          }
          break;
        case 'size_converted':
          productFilter.sizes = productFilter.sizes || [];
          if (!productFilter.sizes.includes(value)) {
            productFilter.sizes.push(value);
          }
          break;
        case 'web_groups':
        case 'mobile_groups':
          if (TAXONOMY_LEVEL_1?.includes(value.toLowerCase())) {
            productFilter.categories = productFilter.categories || [];
            if (!productFilter.categories.includes(value)) {
              productFilter.categories.push(value);
            }
          }

          if (TAXONOMY_LEVEL_2?.includes(value.toLowerCase())) {
            productFilter.productTypes = productFilter.productTypes || [];
            if (!productFilter.productTypes.includes(value)) {
              productFilter.productTypes.push(value);
            }
          }

          if (TAXONOMY_LEVEL_3?.includes(value.toLowerCase())) {
            productFilter.activities = productFilter.activities || [];
            if (!productFilter.activities.includes(value)) {
              productFilter.activities.push(value);
            }
          }

          if (ACTIVIITY_LIST?.includes(value.toLowerCase())) {
            productFilter.activityList = productFilter.activityList || [];
            if (!productFilter.activityList.includes(value)) {
              productFilter.activityList.push(value);
            }
          }
          break;

        default:
          // Ignore unknown filter keys
          break;
      }
    }
  }

  return productFilter;
};
